import { getCurrentScope, onScopeDispose, ref } from "vue";
import { searchAdresse } from "../infrastructure/api/adresseApi";
import { useAppToast } from "@/shared/composables/useAppToast";
/**
 * Shared, API-backed address autocomplete. Single source of truth for the
 * `/organisations/adresse-search` endpoint: 400ms debounce, configurable minimum
 * character threshold (default 3), loading flag, error toast, and city/postalCode/point
 * metadata mapping.
 *
 * Consumed by useAddressForm (create and hydrate modes), AddressFormFields
 * (the shared "add more details" form body) and the contact add/edit-address modals.
 *
 * The 3-char default is a backend contract, not a free UX choice: the BFF
 * `SearchAdresseQueryValidator` enforces `MinimumLength(3)` and the upstream BAN
 * API (api-adresse.data.gouv.fr) rejects shorter queries, so firing below 3 chars
 * only yields a 400 → error toast (OP-6112). Do not pass a value below 3.
 *
 * @param minChars Minimum number of characters before a search fires (default 3, hard floor for the API).
 */
export function useAddressSearch(minChars = 3) {
    const { toastError } = useAppToast();
    const suggestions = ref([]);
    const loading = ref(false);
    let debounce = null;
    // R97: each search (and clear) supersedes every earlier one, so a slow response for an older
    // query never overwrites the suggestions of the text the field holds now.
    let latestRun = 0;
    function search(query) {
        if (debounce)
            clearTimeout(debounce);
        const run = ++latestRun;
        if (query.trim().length < minChars) {
            suggestions.value = [];
            return;
        }
        debounce = setTimeout(async () => {
            loading.value = true;
            try {
                const { results } = await searchAdresse(query, 5);
                if (run !== latestRun)
                    return;
                suggestions.value = results.map((r) => ({
                    value: r.label,
                    label: r.label,
                    meta: {
                        city: r.city,
                        postalCode: r.postalCode,
                        country: "France",
                        latitude: r.latitude,
                        longitude: r.longitude,
                    },
                }));
            }
            catch {
                if (run !== latestRun)
                    return;
                toastError("organisation.create.errors.addressSearchError");
                suggestions.value = [];
            }
            finally {
                if (run === latestRun)
                    loading.value = false;
            }
        }, 400);
    }
    function clear() {
        if (debounce)
            clearTimeout(debounce);
        debounce = null;
        latestRun++;
        suggestions.value = [];
        loading.value = false;
    }
    // A pending debounce outliving its component would fire against a torn-down app (a toast, a
    // re-render), so the timer dies with the scope that owns it.
    if (getCurrentScope())
        onScopeDispose(clear);
    return { suggestions, loading, search, clear };
}
